/**
 * Content Section Scrollable - V2
 * 
 * VARIATION GROUP: Renders all content as visible scrollable sections.
 * 
 * Purpose:
 * - All content sections are visible and scrollable (no hidden tab content)
 * - Each section has an ID for external navigation targeting
 * - Sticky tabs are rendered OUTSIDE this component (at layout level)
 * - This component only provides scroll targets, not navigation
 * 
 * Navigation:
 * - Desktop: StickyNavWithActions (rendered in desktop-layout.tsx)
 * - Mobile: StickyHeaderTabs (rendered in mobile-layout.tsx)
 * 
 * @see content-section-control.tsx for the control with traditional tabs
 */

import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { Stack, Box } from '@mui/material';
import { getTenantById, isTenantExpInService } from '@tenants';
import { useListingRestrictions } from 'hooks/use-listing-restrictions';
import { ComplianceRequirement } from 'types/compliance';
import { CountryCodeList } from 'types/countries';
import { Namespaces } from 'types/locales';
import { getPropertySubTypeLabelKey, getPropertyTypeLabelKey } from 'types/reso';
import LoadingText from 'components/loading-text';
import { VerticalSpacing, VERTICAL_SPACING_VARIANTS } from 'components/atomic/atoms/vertical-spacing';

import type { AddressPageCommonData, AddressPageEventHandlers } from '../../../shared';
import { ListingDetails } from '../../../components/key-facts';
import { FEATURE_FLAGS, hasFeatureFlag } from '../../../types';
import { TAB_BAR_HEIGHT } from 'sections/listing/sticky-header-tabs';
import { useHeaderHeight } from 'hooks/use-header-height';
import {
  OverviewSectionSwitch,
  PriceHistorySectionSwitch,
  HomeAppraisalSectionSwitch,
  MapSectionSwitch,
  CommuteSectionSwitch,
} from 'sections/listing/content-sections';
import { SECTION_IDS } from '../../utils/get-section-id';
import styles from './style.module.scss';
import { useScrollableSection } from '../../hooks/use-scrollable-section';

//#region Dynamic Imports
const ProviderDisclaimer = dynamic(() => import('../../../components/provider-disclaimer'), {
  loading: () => <LoadingText />,
});

const MyListingAgentSection = dynamic(
  () => import('sections/listing/my-listing-agent').then(m => m.MyListingAgentSection),
  { loading: () => <LoadingText />, ssr: false }
);
//#endregion

export interface ContentSectionScrollableProps {
  data: AddressPageCommonData;
  handlers: AddressPageEventHandlers;
  className?: string;
  /** Whether map should allow user interactions (desktop: true, mobile: false) */
  isMapInteractive: boolean;
  /** Whether to show My Listing / My Link agent card (mobile only) */
  showMyListingAgent: boolean;
  /** Viewport identifier for unique section IDs (desktop/mobile) */
  viewport: 'desktop' | 'mobile';
  /** Ref to price history section for scrolling */
  priceHistoryRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Content Section Scrollable
 * 
 * VARIATION GROUP: Renders all content as visible scrollable sections.
 * 
 * Responsibilities:
 * - Viewport-agnostic orchestrator that composes listing sections
 * - All sections are visible (no hidden tab content)
 * - Each section has an ID for external navigation (scroll targeting)
 * - Does NOT render navigation tabs (handled at layout level)
 * - Does NOT make viewport decisions (props passed from layout)
 * 
 * External Navigation:
 * - Desktop: StickyNavWithActions (in desktop-layout.tsx)
 * - Mobile: StickyHeaderTabs (in mobile-layout.tsx)
 * 
 * Viewport Logic:
 * - Layouts (mobile/desktop) determine viewport-specific behavior
 * - This component receives viewport decisions as props
 */
export function ContentSectionScrollable({
  data,
  handlers,
  className,
  isMapInteractive,
  showMyListingAgent,
  viewport,
  priceHistoryRef,
}: ContentSectionScrollableProps) {
  
  const {
    tenantId,
    address,
    listing,
    history,
    appraisal,
    flags,
    shouldScramble,
    complianceRequirement,
    hideSquareFootage,
  } = data;

  const {
    onRoomsShowDetailsClick,
    onContactForPriceClick,
    onViewListingClick,
    onComplianceRequirementClick,
  } = handlers;

  const { getSectionId } = useScrollableSection(viewport);
  const { t } = useTranslation(Namespaces.COMMON);
  const tenant = getTenantById(tenantId);
  const headerHeight = useHeaderHeight();
  
  // Scroll offset for sticky header (header + tab bar + buffer)
  const scrollOffset = headerHeight + TAB_BAR_HEIGHT + 16;
  // Get listing country for restrictions
  const listingCountryCode = address.countryCode.toUpperCase() === CountryCodeList.CANADA 
    ? CountryCodeList.CANADA 
    : CountryCodeList.UNITED_STATES;
  const { canViewWalkScore, canViewPropertyAppraisal } = useListingRestrictions(listingCountryCode);

  // Calculate visibility conditions
  // const hasAddressCoordinates = listing.position?.coordinates?.length === 2;
  // Hide commute section in the sticky tab variation
  const isCommuteTabVisible = false;
  const isPropertyAppraisalVisible = 
    hasFeatureFlag(flags, FEATURE_FLAGS.PROPERTY_APPRAISAL_ENABLED) && 
    canViewPropertyAppraisal && 
    appraisal?.appraisalResult?.isAvailable;

  const fullAddress = address.address;

  const propertyType = t(getPropertyTypeLabelKey(listing.propertyType));
  const propertySubType = t(getPropertySubTypeLabelKey(listing.propertySubType));

  const keyFacts: ListingDetails = useMemo(() => ({
    mlsNum: listing.mlsNum,
    providerId: listing.providerId,
    isCondo: listing.isCondo,
    isRental: listing.isRental,
    isAvailable: listing.isAvailable,
    availableAt: listing.availableAt,
    approxAge: listing.misc?.approxAge,
    daysOnMarket: listing.daysOnMarket,
    lotSize: listing.lotSize,
    maintenanceFees: listing.maintenanceFees,
    taxes: listing.taxes,
    priceBySqft: listing.priceBySqft,
    type: propertySubType,
    levels: propertyType,
    sqftLabel: listing.sqftLabel,
    garage: listing.garage,
  }), [listing, propertyType, propertySubType]);

  const isInServiceTenant = isTenantExpInService(tenantId);
  const listingAgent = useMemo(() => ({
    // Retained for In-Service only: military badges exist on this legacy ListingAgent
    // design and are not incorporated in MyListingAgentCard.
    expAgentId: isInServiceTenant ? listing.expAgentId ?? undefined : undefined,
    showMilitaryDetails: isInServiceTenant,
  }), [listing.expAgentId, isInServiceTenant]);

  const locationUnavailableProps = tenant.theme.style_overrides?.locationUnavailable ?
    {
      src: tenant.theme.style_overrides.locationUnavailable.imagePath,
      message: tenant.theme.style_overrides.locationUnavailable.message,
    } :
    {};

  const isPrivate = complianceRequirement !== ComplianceRequirement.NONE;
  const showStreetLevelLocation = listing.displayAddress !== false;

  return (
    <div 
      className={className}
      style={{ '--scroll-offset': `${scrollOffset}px` } as React.CSSProperties}
    >
      <Box sx={{ px: { xs: 2, md: 0 }}}>
        <Stack spacing={2}>
          {/* Overview Section */}
          <OverviewSectionSwitch
            id={getSectionId(SECTION_IDS.OVERVIEW)}
            tenantId={tenantId}
            keyFacts={keyFacts}
            fullAddress={fullAddress}
            listingAgent={listingAgent}
            listing={listing}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            showWalkScore={canViewWalkScore && hasFeatureFlag(flags, FEATURE_FLAGS.WALK_SCORE_ENABLED)}
            onRoomsShowDetailsClick={onRoomsShowDetailsClick}
            hideSquareFootage={hideSquareFootage}
            showPropertyAddress={showStreetLevelLocation}
          />

          {/* Price History Section */}
          {hasFeatureFlag(flags, FEATURE_FLAGS.PRICE_HISTORY_ENABLED) && !listing.hidePriceHistory && (
            <PriceHistorySectionSwitch
              ref={priceHistoryRef}
              id={getSectionId(SECTION_IDS.PRICE_HISTORY)}
              currentListingId={listing.id}
              isRental={listing.isRental}
              saleHistory={history.filter(h => !h.isRental)}
              rentalHistory={history.filter(h => h.isRental)}
              currentListingData={{
                id: listing.id,
                addressPath: listing.addressPath,
                isRental: listing.isRental,
                price: listing.price,
                soldPrice: listing.soldPrice,
                addedAt: listing.addedAt,
                soldAt: listing.soldAt,
                expiredAt: listing.expiredAt,
                standardStatus: listing.standardStatus,
                providerId: listing.providerId ?? 0,
                complianceRequirement,
                mlsNum: listing.mlsNum,
                thumbnailUrl: listing.thumbnailUrl,
                imageDesc: listing.imageDesc,
              }}
              onComplianceRequirementClick={onComplianceRequirementClick}
              onContactForPriceClick={onContactForPriceClick}
              onViewListingClick={onViewListingClick}
            />
          )}

          {/* My Listing / My Link Agent Card (mobile: below price history) */}
          {showMyListingAgent && <MyListingAgentSection listing={listing} />}

          {/* Provider Disclaimer */}
          {/* When true, hides the courtesy text (useful when it's already shown in hero). This flag value wont be used as intended. Courtesy text shows after mls number and in provider disclaimer section. */}
          {hasFeatureFlag(flags, FEATURE_FLAGS.PROVIDER_DISCLAIMER_ENABLED) && (
            <ProviderDisclaimer 
              listingInfo={listing} 
              tenantId={tenantId}
              hideCourtesyText={false}
            />
          )}

          {/* Home Appraisal Section */}
          {isPropertyAppraisalVisible && (
            <HomeAppraisalSectionSwitch
              id={getSectionId(SECTION_IDS.HOME_APPRAISAL)}
              tenantId={tenantId}
              tenantName={tenant.theme.name}
              price={listing.price}
              daysOnMarket={listing.daysOnMarket}
              isPrivate={isPrivate}
              shouldScramble={shouldScramble}
              appraisalResult={appraisal.appraisalResult}
              sellingRange={appraisal?.sellingRange}
              hideAvm={listing.hideAvm}
              onContactAnAgentClick={appraisal?.onContactAnAgentClick}
            />
          )}

          {/* Map Section */}
          {showStreetLevelLocation && (
            <MapSectionSwitch
              id={getSectionId(SECTION_IDS.MAP)}
              listingId={listing.id}
              position={listing.position}
              fullAddress={fullAddress}
              isInteractive={isMapInteractive}
              locationUnavailableProps={locationUnavailableProps}
              mapClassName={styles.map}
            />
          )}

          {/* Commute Section */}
          {isCommuteTabVisible && (
            <CommuteSectionSwitch
              id={getSectionId(SECTION_IDS.COMMUTE)}
              address={listing}
            />
          )}
        </Stack>
      </Box>
      
      {/* Vertical spacing at the end of content section */}
      <VerticalSpacing variant={VERTICAL_SPACING_VARIANTS.LARGE} />
    </div>
  );
}

export default ContentSectionScrollable;
